import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { get } from '../api/client';
import type { LeadsSummary, PublishSummary } from '../api/types';
import { useLogout, useMe } from '../auth';
import {
  IconBike,
  IconExternal,
  IconHistory,
  IconHome,
  IconInbox,
  IconLogout,
  IconMenu,
  IconNews,
  IconPin,
  IconSections,
  IconText,
  IconUpload,
  IconUser,
  IconUsers,
} from '../components/icons';
import { LangSwitch, useFeedback } from '../components/ui';
import { hasUnsavedChanges } from '../hooks';
import { useT } from '../i18n';
import { BRAND, SITE, SITE_LABELS } from '../site';

/** La API de publicar de esta marca (para el punto de "cambios sin publicar"). */
const PUBLISH_API = SITE === 'phelon-moore' ? '/admin/pm/publish' : '/admin/publish';

export function Shell() {
  const { t } = useT();
  const { data: me } = useMe();
  const logout = useLogout();
  const { confirm } = useFeedback();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const summary = useQuery({
    queryKey: ['leads', 'summary'],
    queryFn: () => get<LeadsSummary>('/admin/leads/summary'),
    refetchInterval: 60_000,
  });
  const unread = summary.data?.new ?? 0;
  const publish = useQuery({
    queryKey: [SITE === 'phelon-moore' ? 'pm-publish' : 'publish', 'summary'],
    queryFn: () => get<PublishSummary>(`${PUBLISH_API}/summary`),
    refetchInterval: 60_000,
  });
  const pending = publish.data ? !publish.data.upToDate : false;
  // Quien entra también al dashboard de la otra marca tiene el enlace a la mano.
  const other = me?.dashboards?.[BRAND.other];

  // En el celular el menú es un panel lateral: se cierra al navegar o con Escape.
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const link = (to: string, label: string, icon: ReactNode, extra?: ReactNode) => (
    <NavLink to={to} end={to === '/'} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
      {icon}
      <span className="label">{label}</span>
      {extra}
    </NavLink>
  );
  const pendingDot = pending && <span className="pending-dot" title={t('Hay cambios sin publicar')} />;

  return (
    <div className={`shell ${menuOpen ? 'menu-open' : ''}`}>
      <header className="topbar">
        <button className="btn btn-ghost btn-icon" onClick={() => setMenuOpen(true)} aria-label={t('Abrir menú')} aria-expanded={menuOpen} aria-controls="menu">
          <IconMenu />
        </button>
        <NavLink to="/" className="brand" aria-label={t('Inicio')}>
          <img src="/logo.svg" alt={BRAND.name} />
        </NavLink>
        <NavLink to="/mensajes" className="topbar-inbox" aria-label={unread ? t('Mensajes: {n} nuevos', { n: unread }) : t('Mensajes')}>
          <IconInbox />
          {unread > 0 && <span className="count">{unread}</span>}
        </NavLink>
      </header>

      <aside className="sidebar" id="menu">
        <NavLink to="/" className="brand" aria-label={t('Inicio')}>
          <img src="/logo.svg" alt={BRAND.name} />
          <span>{t('Dashboard')}</span>
        </NavLink>
        {link('/', t('Inicio'), <IconHome />)}
        {link('/mensajes', t('Mensajes'), <IconInbox />, unread > 0 && <span className="count">{unread}</span>)}

        {SITE === 'phelon-moore' ? (
          <>
            <div className="nav-group">{t('Landing')}</div>
            {link('/motos', t('Motos'), <IconBike />)}
            {link('/noticias', t('Noticias'), <IconNews />)}
            {link('/historia', t('Historia'), <IconHistory />)}
            {link('/secciones', t('Secciones'), <IconSections />)}
            {link('/publicar', t('Publicar'), <IconUpload />, pendingDot)}
          </>
        ) : (
          <>
            <div className="nav-group">{t('Sitio')}</div>
            {link('/motos', t('Motos'), <IconBike />)}
            {link('/puntos-de-venta', t('Puntos de venta'), <IconPin />)}
            {link('/textos', t('Textos'), <IconText />)}
            {link('/publicar', t('Publicar'), <IconUpload />, pendingDot)}
          </>
        )}

        {me?.role === 'ADMIN' && (
          <>
            <div className="nav-group nav-group-sep" aria-hidden="true" />
            {link('/usuarios', t('Usuarios'), <IconUsers />)}
          </>
        )}
        <div className="spacer" />
        {other && (
          <a className="nav-link other-dashboard" href={other}>
            <IconExternal />
            <span className="label">{t('Dashboard de {brand}', { brand: SITE_LABELS[BRAND.other] })}</span>
          </a>
        )}
        <div className="me">
          <div>
            {/* El idioma del dashboard (seis), a la altura del nombre. */}
            <div className="me-name">
              <strong>{me?.name}</strong>
              <LangSwitch />
            </div>
            <small>{me?.email}</small>
          </div>
          <div className="row">
            <NavLink to="/cuenta" className="btn btn-sm" title={t('Mi cuenta')}>
              <IconUser />
              <span className="label">{t('Mi cuenta')}</span>
            </NavLink>
            <button
              className="btn btn-sm btn-ghost"
              title={t('Cerrar sesión')}
              aria-label={t('Cerrar sesión')}
              onClick={async () => {
                if (hasUnsavedChanges() && !(await confirm(t('Hay cambios sin guardar. ¿Cerrar sesión y descartarlos?'), { confirmLabel: t('Cerrar sesión'), danger: true }))) {
                  return;
                }
                await logout();
              }}
            >
              <IconLogout />
            </button>
          </div>
        </div>
      </aside>
      <div className="scrim" onClick={() => setMenuOpen(false)} aria-hidden="true" />

      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
